import BaseClient from './baseClient';

type Dataset_Status = 'INIT' | 'PROCESSING' | 'PUBLISHED';

type Dataset_Item = {
  dataSetKey: string;
  description: string;
  searchTermsSize: number;
  status: Dataset_Status;
  threadsSize: number;
};

export type Dataset_List = {
  dataSets: Dataset_Item[];
};

type Product_Item = {
  threadId: string;
  styleColor: string;
};

export type DatasetProductList = {
  labeledIds: Product_Item[];
  unlabeledIds: Product_Item[];
};

type Product_Node = {
  subType: string;
  properties: {
    portraitURL: string;
    squarishURL: string;
  };
  nodes?: Product_Node[];
};

export type Product_SearchTerm = {
  searchTerm: string;
  recall: boolean | string;
};

export type Product_Detail = {
  fullTitle: string;
  htmlDescription: string;
  slug: string;
  images: string[];
  searchTerms: Array<{
    searchTerm: string;
    recall: boolean;
  }>;
};

type Product_Report = {
  coverage: number;
  labelersCount: number;
  searchTermsSize: number;
  threadsSize: number;
  stats: Array<{
    labelers: Record<string, string>;
    searchTerm: string;
    styleColor: string;
    threadId: string;
  }>;
};

export type Product_Report_Response = Product_Report & {
  productStats: Array<{
    threadId: string;
    labelledAmount: number;
  }>;
};

class LabellingClient extends BaseClient {
  findAllDataset(token: string): Promise<Dataset_List> {
    return this.fetch('/labeling/dataset/v1');
  }

  async findDatasetProducts(
    datasetId: string,
    token: string,
  ): Promise<DatasetProductList> {
    return this.fetch(`/labeling/dataset/${datasetId}/products/v1`);
  }

  async findDatasetProductById(
    datasetId: string,
    productId: string,
    token: string,
  ): Promise<Product_Detail> {
    const res = await this.fetch(
      `/labeling/dataset/${datasetId}/products/${productId}/v1`,
    );

    const productDetail = {
      fullTitle: res.productData.productInfo[0].productContent.fullTitle,
      htmlDescription: res.productData.productInfo[0].productContent.description
        .replace(/<p>敬请注意.+<\/p>/, '')
        .replace(/<br>/, ''),
      slug: res.productData.productInfo[0].productContent.slug,
      images: [],
      searchTerms: res.searchTerms.map((item: Product_SearchTerm) => ({
        searchTerm: item.searchTerm,
        recall: Boolean(item.recall),
      })),
    };
    if (res.productData.publishedContent.nodes) {
      const carouselNodes = res.productData.publishedContent.nodes
        .filter((node: Product_Node) => node.subType === 'carousel')
        .map((node: Product_Node) => node.nodes || []);

      productDetail.images = carouselNodes
        .flat()
        .filter((node: Product_Node) => node.subType === 'image')
        .map(
          (node: Product_Node) =>
            node.properties.squarishURL || node.properties.portraitURL,
        );
    }

    return productDetail;
  }

  labelDatasetProduct(
    datasetId: string,
    productId: string,
    searchTerms: Array<Product_SearchTerm>,
    token: string,
  ) {
    return this.fetch(`/labeling/dataset/${datasetId}/${productId}/v1`, {
      method: 'PATCH',
      body: JSON.stringify({ searchTerms }),
    });
  }

  async findDatasetReportById(
    datasetId: string,
    token: string,
  ): Promise<Product_Report_Response> {
    const res: Product_Report = await this.fetch(
      `/labeling/dataset/${datasetId}/stats/v1`,
    );

    const productMap: Record<string, number> = {}; // {productId: productIndexInProductStats}
    const productLabellerSet = new Set<string>(); // { productId+labllerName }
    const productStats: Array<{ threadId: string; labelledAmount: number }> =
      [];
    res.stats.forEach((state) => {
      if (productMap[state.threadId] === undefined) {
        productStats.push({
          threadId: state.threadId,
          labelledAmount: 0,
        });
        productMap[state.threadId] = productStats.length - 1;
      }
      const labellers = Object.keys(state.labelers);
      if (labellers.length > 0) {
        labellers.forEach((labeller) => {
          const setKey = state.threadId + labeller;
          if (!productLabellerSet.has(setKey)) {
            const productStateIdx = productMap[state.threadId];
            productStats[productStateIdx].labelledAmount++;
            productLabellerSet.add(setKey);
          }
        });
      }
    });

    return { ...res, productStats };
  }

  publishDataset(datasetId: string, token: string) {
    return this.fetch(`/labeling/dataset/${datasetId}/publish/v1`, {
      method: 'POST',
    });
  }
}

export default new LabellingClient();
